import React, { useState, lazy, Suspense } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Phone, Send, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CalendarModal = lazy(() => import('./CalendarModal'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, formData, { timeout: 4000 });
      toast.success(t.contact.form.success);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.warn('Backend API unreachable, redirecting via WhatsApp lead fallback:', error);
      const msg = encodeURIComponent(
        `👋 *NUEVO MENSAJE DE CONTACTO*\n\n` +
        `👤 *Nombre:* ${formData.name}\n` +
        `📧 *Email:* ${formData.email}\n` +
        `📱 *Teléfono:* ${formData.phone || 'N/A'}\n` +
        `🏢 *Empresa:* ${formData.company || 'N/A'}\n` +
        `💼 *Servicio:* ${formData.service || 'General'}\n` +
        `💬 *Mensaje:* ${formData.message}`
      );
      window.open(`https://wa.me/56947127116?text=${msg}`, '_blank');
      toast.success(t.contact.form.success);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } finally {
      setIsLoading(false);
    }
  };

  const services = [
    { value: 'catalogohub', label: 'Catálogo Hub (Catálogos Digitales & WhatsApp)' },
    { value: 'barbershop', label: 'Vegsoft Barber (SaaS Reservas)' },
    { value: 'products', label: 'Creación de Producto Digital / SaaS' },
    { value: 'automation', label: 'Automatización & Agentes IA' },
    { value: 'platforms', label: 'Apps Móviles (iOS / Android)' },
    { value: 'ecommerce', label: 'E-commerce & Plataformas Web' },
    { value: 'innovation', label: 'Prototipado Rápido & Estrategia' }
  ];

  return (
    <>
      <section
        id="contact"
        data-testid="contact-section"
        className="relative py-20 lg:py-28 overflow-hidden bg-white border-t border-slate-100"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="stripe-badge mb-4 inline-flex">
              <span>✉️</span>
              <span>{t.contact.badge}</span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mb-4 tracking-tight text-[#0F172A]">
              {t.contact.title}{' '}
              <span className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">
                {t.contact.titleHighlight}
              </span>
            </h2>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed font-normal">
              {t.contact.subtitle}
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="space-y-4 p-8 rounded-3xl bg-slate-50/50 border border-slate-200/80 shadow-sm"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Input
                      name="name"
                      placeholder={t.contact.form.name}
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name"
                      className="bg-white border-slate-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 h-12 text-[#0F172A] rounded-xl"
                    />
                  </div>
                  <div>
                    <Input
                      name="email"
                      type="email"
                      placeholder={t.contact.form.email}
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email"
                      className="bg-white border-slate-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 h-12 text-[#0F172A] rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Input
                      name="phone"
                      placeholder={t.contact.form.phone}
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone"
                      className="bg-white border-slate-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 h-12 text-[#0F172A] rounded-xl"
                    />
                  </div>
                  <div>
                    <Input
                      name="company"
                      placeholder={t.contact.form.company}
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="contact-company"
                      className="bg-white border-slate-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 h-12 text-[#0F172A] rounded-xl"
                    />
                  </div>
                </div>

                <Select value={formData.service} onValueChange={handleServiceChange}>
                  <SelectTrigger
                    data-testid="contact-service"
                    className="bg-white border-slate-200 focus:border-[#4F46E5] h-12 rounded-xl text-slate-700"
                  >
                    <SelectValue placeholder={t.contact.form.selectService} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Textarea
                  name="message"
                  placeholder={t.contact.form.message}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  data-testid="contact-message"
                  className="bg-white border-slate-200 focus:border-[#4F46E5] min-h-[120px] resize-none text-[#0F172A] rounded-xl"
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="contact-submit"
                  className="w-full btn-stripe-dark h-12 text-sm disabled:opacity-60"
                >
                  {isLoading ? (
                    <span className="animate-pulse">{t.contact.form.sending}</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>{t.contact.form.submit}</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-4">
              {/* Email Card */}
              <div className="p-6 rounded-2xl border border-slate-200/80 bg-white shadow-sm hover:border-slate-300 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0 text-[#4F46E5]">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-[#0F172A] text-sm mb-1">{t.contact.info.email}</h3>
                    <a
                      href="mailto:Douglas.vegas@vegsoftsolutions.com"
                      data-testid="contact-email-link"
                      className="text-slate-500 hover:text-[#4F46E5] transition-colors text-xs sm:text-sm break-all"
                    >
                      Douglas.vegas@vegsoftsolutions.com
                    </a>
                  </div>
                </div>
              </div>

              {/* WhatsApp Card */}
              <div className="p-6 rounded-2xl border border-slate-200/80 bg-white shadow-sm hover:border-emerald-200 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0 text-[#10B981]">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-[#0F172A] text-sm mb-1">{t.contact.info.whatsapp}</h3>
                    <a
                      href="https://wa.me/56947127116"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-link"
                      className="text-slate-500 hover:text-[#10B981] transition-colors text-xs sm:text-sm font-medium"
                    >
                      +56 947 127 116
                    </a>
                  </div>
                </div>
              </div>

              {/* Schedule CTA Box */}
              <div className="p-6 rounded-2xl border border-indigo-100 bg-indigo-50/50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-white border border-indigo-100 flex items-center justify-center text-[#4F46E5]">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <h3 className="font-heading font-bold text-sm text-[#0F172A]">{t.contact.info.schedule}</h3>
                </div>
                <p className="text-xs text-slate-500 mb-4 leading-relaxed font-normal">
                  {language === 'es' ? 'Agenda directamente en nuestro calendario una sesión de 30 minutos sin costo.' : 'Book a 30-min strategy call directly on our calendar at no cost.'}
                </p>
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  data-testid="contact-schedule-btn"
                  className="w-full btn-stripe-dark text-xs py-2.5"
                >
                  {t.contact.info.scheduleBtn}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isCalendarOpen && (
        <Suspense fallback={null}>
          <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default Contact;
